// src/hooks/useUserRole.ts
import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { useSupabase } from "./useSupabase";
import type { UserRole, CompanyMember, ProjectMember } from "../types/domain";

export function useUserRole() {
  const { user } = useSupabase();
  const [userRole, setUserRole] = useState<UserRole>("member");
  const [companyMemberships, setCompanyMemberships] = useState<CompanyMember[]>([]);
  const [projectMemberships, setProjectMemberships] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    loadUserRole();
  }, [user]);

  const loadUserRole = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Check if user is super admin by email (case insensitive)
      const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.toLowerCase().split(',') || [];
      console.log('🔍 Debug Super Admin Check:');
      console.log('- User email:', user.email);
      console.log('- Admin emails from .env:', adminEmails);
      console.log('- Is super admin?', user.email && adminEmails.includes(user.email.toLowerCase()));
      
      if (user.email && adminEmails.includes(user.email.toLowerCase())) {
        console.log('✅ User is Super Admin!');
        setUserRole("super_admin");
        setLoading(false);
        return;
      }

      // Load user profile to get default role
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role) {
        setUserRole(profile.role);
      }

      // Load company memberships
      const { data: companyMembers } = await supabase
        .from('company_members')
        .select(`
          *,
          companies(*),
          profiles(*)
        `)
        .eq('user_id', user.id);

      if (companyMembers) {
        // Transform the data to match the expected structure
        const transformedCompanyMembers = companyMembers.map(item => ({
          ...item,
          company: item.companies,
          user: item.profiles
        }));
        setCompanyMemberships(transformedCompanyMembers as CompanyMember[]);
      }

      // Load project memberships
      const { data: projectMembers } = await supabase
        .from('project_members')
        .select(`
          *,
          projects(*),
          profiles(*)
        `)
        .eq('user_id', user.id);

      if (projectMembers) {
        // Transform the data to match the expected structure
        const transformedProjectMembers = projectMembers.map(item => ({
          ...item,
          project: item.projects,
          user: item.profiles
        }));
        setProjectMemberships(transformedProjectMembers as ProjectMember[]);
      }

    } catch (error) {
      console.error('Error loading user role:', error);
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = () => userRole === "super_admin";
  const isAdmin = () => userRole === "admin" || isSuperAdmin();
  const isMember = () => userRole === "member" && !isAdmin();

  const isAdminOfCompany = (companyId: string) => {
    if (isSuperAdmin()) return true;
    return companyMemberships.some(cm => 
      cm.company_id === companyId && (cm.role === "admin" || cm.role === "super_admin")
    );
  };

  const hasAccessToProject = (projectId: string) => {
    if (isSuperAdmin()) return true;
    return projectMemberships.some(pm => pm.project_id === projectId);
  };

  const canCreateProjects = (companyId?: string) => {
    if (isSuperAdmin()) return true;
    if (!companyId) return isAdmin();
    return isAdminOfCompany(companyId);
  };

  const canManageUsers = (companyId?: string) => {
    if (isSuperAdmin()) return true;
    if (!companyId) return isAdmin();
    return isAdminOfCompany(companyId);
  };

  const canCreateTasks = (projectId?: string) => {
    // All users can create tasks in projects they have access to
    if (!projectId) return true;
    if (isSuperAdmin()) return true;
    return hasAccessToProject(projectId);
  };

  const canAssignTasks = (projectId?: string) => {
    // Only admins can assign tasks to other users
    if (isSuperAdmin()) return true;
    if (!projectId) return isAdmin();
    // Check if user is admin of the company that owns the project
    const project = projectMemberships.find(pm => pm.project_id === projectId)?.project;
    if (project) {
      return isAdminOfCompany(project.company_id);
    }
    return isAdmin();
  };

  return {
    userRole,
    companyMemberships,
    projectMemberships,
    loading,
    isSuperAdmin,
    isAdmin,
    isMember,
    isAdminOfCompany,
    hasAccessToProject,
    canCreateProjects,
    canManageUsers,
    canCreateTasks,
    canAssignTasks,
    reloadRole: loadUserRole
  };
}